import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/authService";
import { customToast } from '../utils/toastUtils';
import { db } from '../firebase/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import logo from '../assets/ResumeGlobeLogo.svg';
import "./navbar.css";

const Navbar = ({ currentUser, authLoading, onLoginClick, onLogout, onFileUploadClick, onAtsClick, onAboutClick, onCoverLetterClick, onAtsCheckClick, onHomeClick, currentPage }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [navVisible, setNavVisible] = useState(true);
  const [userPlanData, setUserPlanData] = useState(null);
  const dropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);
  const mobileProfileRef = useRef(null);

  // Fetch user plan data from Firestore
  useEffect(() => {
    const fetchUserPlan = async () => {
      if (!currentUser) {
        setUserPlanData(null);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserPlanData({
            planType: userData.planType || 'free',
            hasMockInterview: userData.hasMockInterview || false,
            hasHRConsultancy: userData.hasHRConsultancy || false,
            hasAICoaching: userData.hasAICoaching || false
          });
        } else {
          setUserPlanData({
            planType: 'free',
            hasMockInterview: false,
            hasHRConsultancy: false,
            hasAICoaching: false
          });
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
        setUserPlanData({ planType: 'free', hasMockInterview: false });
      }
    };

    fetchUserPlan();
  }, [currentUser]);

  // Get plan info with fallback to 'free'
  const userPlan = userPlanData?.planType || 'free';
  
  // Check if user has access to premium features (Gold or Platinum plans)
  const hasPremiumAccess = userPlan === 'gold' || userPlan === 'platinum';
  
  // Gold and Platinum users have access to AI Career Toolkit
  const hasAIToolkitAccess = userPlan === 'gold' || userPlan === 'platinum';
  // AI Career Map is Platinum only
  const hasAICareerMapAccess = userPlan === 'platinum';
  
  // Take MCQ test is available only to Platinum users
  const hasMockInterviewAccess = userPlan === 'platinum';

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Add scrolled class when scrolled past initial position
      setIsScrolled(currentScrollY > 10);
      
      // Keep navbar always visible - removed hide/show behavior
      setNavVisible(true); // Always visible
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideDesktop = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const isOutsideMobile = mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target);
      const isOutsideMobileProfile = mobileProfileRef.current && !mobileProfileRef.current.contains(event.target);
      
      // Close dropdown only if click is outside all dropdown-related elements
      if (isOutsideDesktop && isOutsideMobile && isOutsideMobileProfile) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNavClick = (handler) => {
    setMenuOpen(false);
    if (handler) {
      handler();
    }
  };

  const handleLogoClick = () => {
    setMenuOpen(false);
    if (currentPage !== 'home' && onHomeClick) {
      // Navigate to home page if not already there
      onHomeClick();
    } else {
      // Scroll to top if already on home
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSignOut = async () => {
    try {
      setShowDropdown(false);
      
      const result = await authService.logoutUser();
      
      if (result.success) {
        customToast.success('Signed out successfully');
        if (onLogout) {
          onLogout();
        }
        // Redirect to home page
        navigate('/');
      } else {
        customToast.error('Signout failed. Please try again.');
      }
    } catch (error) {
      console.error('❌ Signout error:', error);
      customToast.error('Something went wrong during signout.');
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(prev => !prev);
  };

  const getUserDisplayName = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName;
    }
    if (currentUser?.email) {
      return currentUser.email.split('@')[0];
    }
    if (currentUser?.phoneNumber) {
      return `User ${currentUser.phoneNumber.slice(-4)}`;
    }
    return 'User';
  };

  const getUserAvatar = () => {
    if (currentUser?.photoURL) {
      return currentUser.photoURL;
    }
    
    const name = getUserDisplayName();
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1A8063&color=fff&size=40`;
  };

  const handleAboutClick = () => {
    if (onAboutClick) {
      onAboutClick();
    }
  };

  const handleCoverLetterClick = () => {
    if (onCoverLetterClick) {
      onCoverLetterClick();
    }
  };

  const handleAtsClick = () => {
    if (onAtsCheckClick) {
      onAtsCheckClick();
    }
  };

  // Show loading state
  if (authLoading) {
    return (
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''} ${navVisible ? 'visible' : 'hidden'}`}>
        <div className="nav-content">
          <div className="nav-logo">
            <div className="logo-container">
              <img src={logo} alt="ResumeGlobe" className="logo-image" />
              <span className="logo-tagline">Where Dreams Resume</span>
            </div>
          </div>
          
          <div className="nav-actions">
            <div className="auth-loading">
              <div className="loading-spinner"></div>
              <span>Loading...</span>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Show authenticated user
  if (currentUser) {
    return (
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''} ${navVisible ? 'visible' : 'hidden'}`}>
        <div className="nav-content">
          <div className="nav-logo" onClick={handleLogoClick}>
            <div className="logo-container">
              <img src={logo} alt="ResumeGlobe" className="logo-image" />
              <span className="logo-tagline">Where Dreams Resume</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="nav-items desktop-only">
            {currentPage !== 'templates' && currentPage !== 'editor' && (
              <button className="nav-link transparent" onClick={handleAboutClick}>
                About Us
              </button>
            )}

            {currentPage !== 'templates' && currentPage !== 'editor' && (
              <button className="nav-link transparent" onClick={() => navigate('/templates')}>
                Resume Builder
              </button>
            )}
            
            {currentPage !== 'templates' && currentPage !== 'editor' && (
              <button className="nav-link transparent" onClick={handleCoverLetterClick}>
                Cover Letter
              </button>
            )}
            
            {currentPage !== 'templates' && currentPage !== 'editor' && (
              <button className="nav-link ats-score-btn" onClick={handleAtsClick}>
                Check ATS Score
              </button>
            )}
            
            {/* Desktop Profile Trigger with Dropdown */}
            <div className="user-profile-container desktop-profile-trigger" ref={dropdownRef}>
              <div 
                className="user-profile-trigger"
                onClick={toggleDropdown}
              >
                <img 
                  src={getUserAvatar()}
                  alt="Profile"
                  className="profile-avatar"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=User&background=1A8063&color=fff&size=40`;
                  }}
                />
                <div className="user-info">
                  <span className="user-name">
                    {getUserDisplayName()}
                  </span>
                  <span className="user-email">
                    {currentUser.email || currentUser.phoneNumber || ''}
                  </span>
                </div>
                <div className={`dropdown-arrow ${showDropdown ? 'open' : ''}`}>
                  ▼
                </div>
              </div>

              {/* Desktop Dropdown */}
              {showDropdown && (
                <div className="profile-dropdown desktop-dropdown">
                  <div className="dropdown-header">
                    <img 
                      src={getUserAvatar()}
                      alt="Profile"
                      className="dropdown-avatar"
                    />
                    <div className="dropdown-user-info">
                      <div className="dropdown-name">{getUserDisplayName()}</div>
                      <div className="dropdown-email">
                        {currentUser.email || currentUser.phoneNumber || 'No contact info'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="dropdown-divider"></div>
                  
                  <div className="dropdown-menu">
                    {/* I. About Yourself - Enhanced User Profile Icon */}
                    <button 
                      className="dropdown-item"
                      onClick={() => {
                        setShowDropdown(false);
                        window.location.href = '/profile';
                      }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="8" r="4" fill="url(#blueGradient1)"/>
                        <path d="M20 19v1a1 1 0 01-1 1H5a1 1 0 01-1-1v-1c0-2.66 5.33-4 8-4s8 1.34 8 4z" fill="url(#blueGradient1)"/>
                        <defs>
                          <linearGradient id="blueGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6"/>
                            <stop offset="100%" stopColor="#60a5fa"/>
                          </linearGradient>
                        </defs>
                      </svg>
                      <span>About Yourself</span>
                    </button>
                    
                    {/* II. My Files - Enhanced Folder Icon */}
                    <button 
                      className="dropdown-item"
                      onClick={() => {
                        setShowDropdown(false);
                        window.location.href = '/my-files';
                      }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z" fill="url(#orangeGradient)"/>
                        <path d="M20 8H4v10h16V8z" fill="url(#yellowGradient)" opacity="0.8"/>
                        <circle cx="8" cy="13" r="1.5" fill="white" opacity="0.6"/>
                        <circle cx="12" cy="13" r="1.5" fill="white" opacity="0.6"/>
                        <circle cx="16" cy="13" r="1.5" fill="white" opacity="0.6"/>
                        <defs>
                          <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#f59e0b"/>
                            <stop offset="100%" stopColor="#d97706"/>
                          </linearGradient>
                          <linearGradient id="yellowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#fbbf24"/>
                            <stop offset="100%" stopColor="#f59e0b"/>
                          </linearGradient>
                        </defs>
                      </svg>
                      <span>My Files</span>
                    </button>
                    
                    {/* III. Cover Letter Builder - Enhanced Document Icon */}
                    <button 
                      className="dropdown-item"
                      onClick={() => {
                        setShowDropdown(false);
                        navigate('/cover-letter-templates');
                      }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z" fill="url(#purpleGradient)"/>
                        <path d="M14 2v6h6" fill="url(#purpleLightGradient)"/>
                        <line x1="8" y1="11" x2="13" y2="11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="8" y1="14" x2="16" y2="14" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="8" y1="17" x2="16" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                        <defs>
                          <linearGradient id="purpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#8b5cf6"/>
                            <stop offset="100%" stopColor="#6d28d9"/>
                          </linearGradient>
                          <linearGradient id="purpleLightGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#a78bfa"/>
                            <stop offset="100%" stopColor="#8b5cf6"/>
                          </linearGradient>
                        </defs>
                      </svg>
                      <span>Cover Letter Builder</span>
                    </button>
                    
                    <div className="dropdown-divider"></div>
                    
                    {/* IV. AI Career Toolkit - Enhanced Toolkit Icon */}
                    <button 
                      className={`dropdown-item ${!hasAIToolkitAccess ? 'locked' : ''}`}
                      onClick={() => {
                        if (hasAIToolkitAccess) {
                          setShowDropdown(false);
                          window.location.href = '/ai-improvement/ai-career-toolkit';
                        } else {
                          customToast.warning('Upgrade to Gold or Platinum plan to use AI Career Toolkit');
                        }
                      }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <rect x="2" y="7" width="20" height="14" rx="2" fill="url(#greenGradient)"/>
                        <path d="M20 7V5c0-1.1-.9-2-2-2h-2c-1.1 0-2 .9-2 2v2" stroke="url(#greenLightGradient)" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M9 3v4" stroke="url(#greenLightGradient)" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="9" cy="14" r="2" fill="white"/>
                        <circle cx="15" cy="14" r="2" fill="white"/>
                        <path d="M9 16l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <defs>
                          <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#10b981"/>
                            <stop offset="100%" stopColor="#059669"/>
                          </linearGradient>
                          <linearGradient id="greenLightGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#34d399"/>
                            <stop offset="100%" stopColor="#10b981"/>
                          </linearGradient>
                        </defs>
                      </svg>
                      <span>AI Career Toolkit</span>
                      {!hasAIToolkitAccess && (
                        <span className="lock-icon" aria-hidden="true">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/>
                          </svg>
                        </span>
                      )}
                    </button>
                    
                    <div className="dropdown-divider"></div>
                    
                    {/* VI. AI Career Map */}
                    <button 
                      className={`dropdown-item ${!hasAICareerMapAccess ? 'locked' : ''}`}
                      onClick={() => {
                        if (hasAICareerMapAccess) {
                          setShowDropdown(false);
                          window.location.href = '/ai-improvement/ai-career-map';
                        } else {
                          customToast.warning('Upgrade to Platinum plan to use AI Career Map');
                        }
                      }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="url(#pinkGradient)" strokeWidth="2" fill="none"/>
                        <path d="M12 2v20M2 12h20" stroke="url(#pinkLightGradient)" strokeWidth="1.5"/>
                        <circle cx="12" cy="6" r="2.5" fill="url(#pinkGradient)"/>
                        <circle cx="12" cy="12" r="3" fill="url(#pinkLightGradient)"/>
                        <circle cx="12" cy="18" r="2.5" fill="url(#pinkGradient)"/>
                        <path d="M12 8.5v1M12 15v0.5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                        <defs>
                          <linearGradient id="pinkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#ec4899"/>
                            <stop offset="100%" stopColor="#be185d"/>
                          </linearGradient>
                          <linearGradient id="pinkLightGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#f472b6"/>
                            <stop offset="100%" stopColor="#ec4899"/>
                          </linearGradient>
                        </defs>
                      </svg>
                      <span>AI Career Map</span>
                      {!hasAICareerMapAccess && (
                        <span className="lock-icon" aria-hidden="true">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/>
                          </svg>
                        </span>
                      )}
                    </button>
                    
                    <div className="dropdown-divider"></div>
                    
                    {/* VII. Take MCQ Test - PREMIUM (Platinum Only) */}
                    <button 
                      className={`dropdown-item ${!hasMockInterviewAccess ? 'locked' : ''}`}
                      onClick={() => {
                        if (hasMockInterviewAccess) {
                          setShowDropdown(false);
                          window.location.href = '/ai-improvement/take-mock-interview';
                        } else {
                          customToast.warning('Upgrade to Platinum plan to use Take MCQ Test');
                        }
                      }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" fill="url(#redGradient)"/>
                        <path d="M12 7v5.5l3.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="2" fill="white"/>
                        <path d="M8 3.5a9 9 0 018 0M8 20.5a9 9 0 008 0" stroke="url(#redDarkGradient)" strokeWidth="1.8" strokeLinecap="round"/>
                        <defs>
                          <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#ef4444"/>
                            <stop offset="100%" stopColor="#dc2626"/>
                          </linearGradient>
                          <linearGradient id="redDarkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#dc2626"/>
                            <stop offset="100%" stopColor="#991b1b"/>
                          </linearGradient>
                        </defs>
                      </svg>
                      <span>Take MCQ Test</span>
                      {!hasMockInterviewAccess && (
                        <span className="lock-icon" aria-hidden="true">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/>
                          </svg>
                        </span>
                      )}
                    </button>
                    
                    <div className="dropdown-divider"></div>
                    
                    {/* VIII. Logout - Enhanced Exit Icon */}
                    <button 
                      className="dropdown-item danger"
                      onClick={handleSignOut}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3 3h11v5M3 21h11v-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                        <circle cx="9" cy="12" r="1.5" fill="currentColor" opacity="0.3"/>
                      </svg>
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Profile Dropdown - Shows only on mobile */}
          <div className="user-profile-container mobile-dropdown-container" ref={mobileDropdownRef}>
            {showDropdown && (
              <div className="profile-dropdown mobile-dropdown">
                <div className="dropdown-header">
                  <img 
                    src={getUserAvatar()}
                    alt="Profile"
                    className="dropdown-avatar"
                  />
                  <div className="dropdown-user-info">
                    <div className="dropdown-name">{getUserDisplayName()}</div>
                    <div className="dropdown-email">
                      {currentUser.email || currentUser.phoneNumber || 'No contact info'}
                    </div>
                  </div>
                </div>
                
                <div className="dropdown-divider"></div>
                
                <div className="dropdown-menu">
                  {/* I. About Yourself */}
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowDropdown(false);
                      window.location.href = '/profile';
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="8" r="4" fill="url(#blueGradient1Mobile)"/>
                      <path d="M20 19v1a1 1 0 01-1 1H5a1 1 0 01-1-1v-1c0-2.66 5.33-4 8-4s8 1.34 8 4z" fill="url(#blueGradient1Mobile)"/>
                      <defs>
                        <linearGradient id="blueGradient1Mobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#3b82f6"/>
                          <stop offset="100%" stopColor="#60a5fa"/>
                        </linearGradient>
                      </defs>
                    </svg>
                    <span>About Yourself</span>
                  </button>
                  
                  {/* II. My Files */}
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowDropdown(false);
                      window.location.href = '/my-files';
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z" fill="url(#orangeGradientMobile)"/>
                      <path d="M20 8H4v10h16V8z" fill="url(#yellowGradientMobile)" opacity="0.8"/>
                      <circle cx="8" cy="13" r="1.5" fill="white" opacity="0.6"/>
                      <circle cx="12" cy="13" r="1.5" fill="white" opacity="0.6"/>
                      <circle cx="16" cy="13" r="1.5" fill="white" opacity="0.6"/>
                      <defs>
                        <linearGradient id="orangeGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#f59e0b"/>
                          <stop offset="100%" stopColor="#d97706"/>
                        </linearGradient>
                        <linearGradient id="yellowGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#fbbf24"/>
                          <stop offset="100%" stopColor="#f59e0b"/>
                        </linearGradient>
                      </defs>
                    </svg>
                    <span>My Files</span>
                  </button>
                  
                  {/* III. Cover Letter Builder */}
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowDropdown(false);
                      navigate('/cover-letter-templates');
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z" fill="url(#purpleGradientMobile)"/>
                      <path d="M14 2v6h6" fill="url(#purpleLightGradientMobile)"/>
                      <line x1="8" y1="11" x2="13" y2="11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="8" y1="14" x2="16" y2="14" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="8" y1="17" x2="16" y2="17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                      <defs>
                        <linearGradient id="purpleGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#8b5cf6"/>
                          <stop offset="100%" stopColor="#6d28d9"/>
                        </linearGradient>
                        <linearGradient id="purpleLightGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#a78bfa"/>
                          <stop offset="100%" stopColor="#8b5cf6"/>
                        </linearGradient>
                      </defs>
                    </svg>
                    <span>Cover Letter Builder</span>
                  </button>
                  
                  <div className="dropdown-divider"></div>
                  
                  {/* IV. AI Career Toolkit */}
                  <button 
                    className={`dropdown-item ${!hasAIToolkitAccess ? 'locked' : ''}`}
                    onClick={() => {
                      if (hasAIToolkitAccess) {
                        setShowDropdown(false);
                        window.location.href = '/ai-improvement/ai-career-toolkit';
                      } else {
                        customToast.warning('Upgrade to Gold or Platinum plan to use AI Career Toolkit');
                      }
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <rect x="2" y="7" width="20" height="14" rx="2" fill="url(#greenGradientMobile)"/>
                      <path d="M20 7V5c0-1.1-.9-2-2-2h-2c-1.1 0-2 .9-2 2v2" stroke="url(#greenLightGradientMobile)" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M9 3v4" stroke="url(#greenLightGradientMobile)" strokeWidth="2" strokeLinecap="round"/>
                      <circle cx="9" cy="14" r="2" fill="white"/>
                      <circle cx="15" cy="14" r="2" fill="white"/>
                      <path d="M9 16l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <defs>
                        <linearGradient id="greenGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#10b981"/>
                          <stop offset="100%" stopColor="#059669"/>
                        </linearGradient>
                        <linearGradient id="greenLightGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#34d399"/>
                          <stop offset="100%" stopColor="#10b981"/>
                        </linearGradient>
                      </defs>
                    </svg>
                    <span>AI Career Toolkit</span>
                    {!hasAIToolkitAccess && (
                      <span className="lock-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/>
                        </svg>
                      </span>
                    )}
                  </button>
                  
                  <div className="dropdown-divider"></div>
                  
                  {/* VI. AI Career Map */}
                  <button 
                    className={`dropdown-item ${!hasAICareerMapAccess ? 'locked' : ''}`}
                    onClick={() => {
                      if (hasAICareerMapAccess) {
                        setShowDropdown(false);
                        window.location.href = '/ai-improvement/ai-career-map';
                      } else {
                        customToast.warning('Upgrade to Platinum plan to use AI Career Map');
                      }
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="url(#pinkGradientMobile)" strokeWidth="2" fill="none"/>
                      <path d="M12 2v20M2 12h20" stroke="url(#pinkLightGradientMobile)" strokeWidth="1.5"/>
                      <circle cx="12" cy="6" r="2.5" fill="url(#pinkGradientMobile)"/>
                      <circle cx="12" cy="12" r="3" fill="url(#pinkLightGradientMobile)"/>
                      <circle cx="12" cy="18" r="2.5" fill="url(#pinkGradientMobile)"/>
                      <path d="M12 8.5v1M12 15v0.5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                      <defs>
                        <linearGradient id="pinkGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#ec4899"/>
                          <stop offset="100%" stopColor="#be185d"/>
                        </linearGradient>
                        <linearGradient id="pinkLightGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#f472b6"/>
                          <stop offset="100%" stopColor="#ec4899"/>
                        </linearGradient>
                      </defs>
                    </svg>
                    <span>AI Career Map</span>
                    {!hasAICareerMapAccess && (
                      <span className="lock-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/>
                        </svg>
                      </span>
                    )}
                  </button>
                  
                  <div className="dropdown-divider"></div>
                  
                  {/* VII. Take MCQ Test */}
                  <button 
                    className={`dropdown-item ${!hasMockInterviewAccess ? 'locked' : ''}`}
                    onClick={() => {
                      if (hasMockInterviewAccess) {
                        setShowDropdown(false);
                        window.location.href = '/ai-improvement/take-mock-interview';
                      } else {
                        customToast.warning('Upgrade to Platinum plan to use Take Mock Interview Test');
                      }
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="url(#redGradientMobile)"/>
                      <path d="M12 7v5.5l3.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="2" fill="white"/>
                      <path d="M8 3.5a9 9 0 018 0M8 20.5a9 9 0 008 0" stroke="url(#redDarkGradientMobile)" strokeWidth="1.8" strokeLinecap="round"/>
                      <defs>
                        <linearGradient id="redGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#ef4444"/>
                          <stop offset="100%" stopColor="#dc2626"/>
                        </linearGradient>
                        <linearGradient id="redDarkGradientMobile" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#dc2626"/>
                          <stop offset="100%" stopColor="#991b1b"/>
                        </linearGradient>
                      </defs>
                    </svg>
                    <span>Take MCQ Test</span>
                    {!hasMockInterviewAccess && (
                      <span className="lock-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/>
                        </svg>
                      </span>
                    )}
                  </button>
                  
                  <div className="dropdown-divider"></div>
                  
                  {/* VIII. Logout */}
                  <button 
                    className="dropdown-item danger"
                    onClick={handleSignOut}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M3 3h11v5M3 21h11v-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                      <circle cx="9" cy="12" r="1.5" fill="currentColor" opacity="0.3"/>
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Hamburger Button and Profile */}
          <div className="mobile-right-section">
            <div className="mobile-profile-avatar" onClick={toggleDropdown} ref={mobileProfileRef}>
              <img 
                src={getUserAvatar()}
                alt="Profile"
                className="profile-avatar"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=User&background=1A8063&color=fff&size=40`;
                }}
              />
            </div>
            
            <button 
              className="mobile-menu-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span className={menuOpen ? 'active' : ''}></span>
              <span className={menuOpen ? 'active' : ''}></span>
              <span className={menuOpen ? 'active' : ''}></span>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
            <div className="mobile-nav-links">
              {currentPage !== 'templates' && currentPage !== 'editor' && (
                <>
                  <button className="mobile-nav-link" onClick={() => handleNavClick(handleAboutClick)}>
                    About Us
                  </button>
                  <button className="mobile-nav-link" onClick={() => handleNavClick(() => navigate('/templates'))}>
                    Resume Builder
                  </button>
                  <button className="mobile-nav-link" onClick={() => handleNavClick(handleCoverLetterClick)}>
                    Cover Letter
                  </button>
                </>
              )}
            </div>
            
            {currentPage !== 'templates' && currentPage !== 'editor' && (
              <div className="mobile-action-buttons">
                <button className="mobile-ats-btn" onClick={() => handleNavClick(handleAtsClick)}>
                  Check ATS Score
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
    );
  }

  // Show login button
  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''} ${navVisible ? 'visible' : 'hidden'}`}>
      <div className="nav-content">
        <div className="nav-logo" onClick={handleLogoClick}>
          <div className="logo-container">
            <img src={logo} alt="ResumeGlobe" className="logo-image" />
            <span className="logo-tagline">Where Dreams Resume</span>
          </div>
        </div>
        
        {/* Desktop Navigation */}
        <div className="nav-items desktop-nav">
          {currentPage !== 'templates' && currentPage !== 'editor' && (
            <button className="nav-link transparent" onClick={handleAboutClick}>
              About Us
            </button>
          )}

          {currentPage !== 'templates' && currentPage !== 'editor' && (
            <button className="nav-link transparent" onClick={() => navigate('/templates')}>
              Resume Builder
            </button>
          )}
          
          {currentPage !== 'templates' && currentPage !== 'editor' && (
            <button className="nav-link transparent" onClick={handleCoverLetterClick}>
              Cover Letter
            </button>
          )}
          
          {currentPage !== 'templates' && currentPage !== 'editor' && (
            <button className="nav-link ats-score-btn" onClick={handleAtsClick}>
              Check ATS Score
            </button>
          )}
          
          <button 
            className="login-signup-btn"
            onClick={onLoginClick}
          >
            Login / Sign Up
          </button>
        </div>

        {/* Mobile Hamburger Button */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${menuOpen ? 'open' : ''}`}></span>
        </button>
      </div>

      {/* Mobile Dropdown Menu */}
      {menuOpen && (
        <div className="mobile-menu-dropdown">
          {/* Navigation Links */}
          <div className="mobile-nav-links">
            {currentPage !== 'templates' && currentPage !== 'editor' && (
              <button className="mobile-nav-link" onClick={() => {
                handleAboutClick();
                setMenuOpen(false);
              }}>
                About
              </button>
            )}

            {currentPage !== 'templates' && currentPage !== 'editor' && (
              <button className="mobile-nav-link" onClick={() => {
                navigate('/templates');
                setMenuOpen(false);
              }}>
                Resume Builder
              </button>
            )}
            
            {currentPage !== 'templates' && currentPage !== 'editor' && (
              <button className="mobile-nav-link" onClick={() => {
                handleCoverLetterClick();
                setMenuOpen(false);
              }}>
                Cover Letter
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mobile-action-buttons">
            {currentPage !== 'templates' && currentPage !== 'editor' && (
              <button 
                className="mobile-ats-btn"
                onClick={() => {
                  handleAtsClick();
                  setMenuOpen(false);
                }}
              >
                Check ATS Score
              </button>
            )}
            
            <button 
              className="mobile-login-btn"
              onClick={() => {
                onLoginClick();
                setMenuOpen(false);
              }}
            >
              Login / Sign Up
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;